import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Load theme from localStorage; migrate legacy 'light' to 'blue-galaxy'
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light') return 'blue-galaxy';
    return savedTheme || 'blue-galaxy';
  });

  useEffect(() => {
    // Apply theme to document (support both html and body for robust theming)
    document.documentElement.setAttribute('data-theme', theme);
    if (document.body) {
      document.body.setAttribute('data-theme', theme);
      // Keep legacy GoodVibes class only when GoodVibes is active; remove otherwise
      if (theme === 'goodvibes') {
        document.body.classList.add('goodvibes');
      } else {
        document.body.classList.remove('goodvibes');
      }
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  // Generic setter for arbitrary themes
  const setThemeName = (name) => setTheme(name);

  // Backward-compatible helpers
  const setLightTheme = () => setTheme('light');
  const setDarkTheme = () => setTheme('dark');

  const value = {
    theme,
    toggleTheme,
    setTheme: setThemeName,
    setLightTheme,
    setDarkTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};